import React, { useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Collapse } from "reactstrap"
import { Formik } from "formik"
import * as Yup from "yup"
import Switch from "react-switch"
import { VendorFormValues } from "../types"

interface AddNewVendorProps {
  isOpen: boolean
  toggle: () => void
}

const AddNewVendor: React.FC<AddNewVendorProps> = ({ isOpen, toggle }) => {
  const [basicInfoOpen, setBasicInfoOpen] = useState(true)
  const [verificationOpen, setVerificationOpen] = useState(false)
  const [paymentsOpen, setPaymentsOpen] = useState(false)
  const [visibilityOpen, setVisibilityOpen] = useState(false)

  const validationSchema = Yup.object({
    vendorName: Yup.string().required("Vendor name is required"),
    companyName: Yup.string().required("Company name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: Yup.string().required("Phone number is required"),
  })

  const initialValues: VendorFormValues = {
    vendorName: "",
    companyName: "",
    contactPerson: "",
    email: "",
    phoneNumber: "",
    location: "",
    paymentMethod: "",
    accountNumber: "",
    commissionRateBundle: "",
    commissionRateOneTime: "",
    payoutCycle: "Monthly",
    additionalNotes: "",
    vendorVisibility: true,
    verifiedVendor: false,
    autoApprovalProducts: false,
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>Add New Vendor</ModalHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          console.log("Form values:", values)
          // TODO: Submit to API
          toggle()
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              {/* Basic Info Section */}
              <div className="border rounded-3 p-3 mb-3">
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setBasicInfoOpen(!basicInfoOpen)}
                >
                  <h5 className="mb-0 fw-semibold">Basic Info</h5>
                  <i className={`bx bx-chevron-${basicInfoOpen ? "up" : "down"} font-size-20`}></i>
                </div>
                <Collapse isOpen={basicInfoOpen}>
                  <Row className="mt-3">
                    <Col md="6">
                      <FormGroup>
                        <Label>Vendor Name <span className="text-danger">*</span></Label>
                        <Input
                          name="vendorName"
                          placeholder="Enter Vendor Name"
                          value={values.vendorName}
                          onChange={handleChange}
                          invalid={touched.vendorName && !!errors.vendorName}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Company Name <span className="text-danger">*</span></Label>
                        <Input
                          name="companyName"
                          placeholder="Enter Business Name"
                          value={values.companyName}
                          onChange={handleChange}
                          invalid={touched.companyName && !!errors.companyName}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label>Contact Person</Label>
                        <Input
                          name="contactPerson"
                          placeholder="Full Name"
                          value={values.contactPerson}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Email <span className="text-danger">*</span></Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="email@example.com"
                          value={values.email}
                          onChange={handleChange}
                          invalid={touched.email && !!errors.email}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Phone Number <span className="text-danger">*</span></Label>
                        <Input
                          name="phoneNumber"
                          placeholder="+974 1234 5678"
                          value={values.phoneNumber}
                          onChange={handleChange}
                          invalid={touched.phoneNumber && !!errors.phoneNumber}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Location / City</Label>
                        <div className="d-flex">
                          <Input
                            type="select"
                            name="location"
                            value={values.location}
                            onChange={handleChange}
                            className="flex-grow-1"
                          >
                            <option value="">Select Location</option>
                            <option value="Doha">Doha</option>
                            <option value="Al Rayyan">Al Rayyan</option>
                            <option value="Al Wakrah">Al Wakrah</option>
                            <option value="Um Salal">Um Salal</option>
                            <option value="Al Khor">Al Khor</option>
                          </Input>
                          <Button color="light" className="ms-2">
                            <i className="bx bx-map"></i>
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Logo Upload</Label>
                        <div className="file-upload-area">
                          <i className="bx bx-cloud-upload"></i>
                          <p className="mb-2 font-size-14">Upload Image</p>
                          <Input 
                            type="file" 
                            className="d-none" 
                            id="logoUpload"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                setFieldValue("logo", e.target.files[0])
                              }
                            }}
                          />
                          <Label for="logoUpload" className="btn btn-sm btn-primary mt-2 mb-0">
                            Choose File
                          </Label>
                          <p className="text-muted font-size-11 mt-2 mb-0">
                            Use high-quality, clear images that focus on the logo with a plain background. Recommended 800x600px
                          </p>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Collapse>
              </div>

              {/* Verification Info Section */}
              <div className="border rounded-3 p-3 mb-3">
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setVerificationOpen(!verificationOpen)}
                >
                  <h5 className="mb-0 fw-semibold">Verification Info</h5>
                  <i className={`bx bx-chevron-${verificationOpen ? "up" : "down"} font-size-20`}></i>
                </div>
                <Collapse isOpen={verificationOpen}>
                  <div className="mt-3">
                    <FormGroup>
                      <Label>Commercial Registration Upload</Label>
                      <div className="border rounded p-2 bg-light">
                        <Input type="file" className="form-control-sm" accept=".pdf,.jpg,.jpeg,.png" />
                        <p className="text-muted font-size-11 mt-1 mb-0">
                          Use high-quality, clear images 800x600px
                        </p>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Commercial Permit Upload</Label>
                      <div className="border rounded p-2 bg-light">
                        <Input type="file" className="form-control-sm" accept=".pdf,.jpg,.jpeg,.png" />
                        <p className="text-muted font-size-11 mt-1 mb-0">
                          Use high-quality, clear images 800x600px
                        </p>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Tax Registration Upload</Label>
                      <div className="border rounded p-2 bg-light">
                        <Input type="file" className="form-control-sm" accept=".pdf,.jpg,.jpeg,.png" />
                        <p className="text-muted font-size-11 mt-1 mb-0">
                          Use high-quality, clear images 800x600px
                        </p>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label>Computer Card Upload</Label>
                      <div className="border rounded p-2 bg-light">
                        <Input type="file" className="form-control-sm" accept=".pdf,.jpg,.jpeg,.png" />
                        <p className="text-muted font-size-11 mt-1 mb-0">
                          Use high-quality, clear images 800x600px
                        </p>
                      </div>
                    </FormGroup>
                  </div>
                </Collapse>
              </div>

              {/* Payments Section */}
              <div className="border rounded-3 p-3 mb-3">
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPaymentsOpen(!paymentsOpen)}
                >
                  <h5 className="mb-0 fw-semibold">Payments</h5>
                  <i className={`bx bx-chevron-${paymentsOpen ? "up" : "down"} font-size-20`}></i>
                </div>
                <Collapse isOpen={paymentsOpen}>
                  <Row className="mt-3">
                    <Col md="6">
                      <FormGroup>
                        <Label>Preferred Payment Method</Label>
                        <Input 
                          type="select"
                          name="paymentMethod"
                          value={values.paymentMethod}
                          onChange={handleChange}
                        >
                          <option value="">Payment Method</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cash">Cash</option>
                          <option value="Check">Check</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Account Number</Label>
                        <Input
                          name="accountNumber"
                          placeholder="Account Number"
                          value={values.accountNumber}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          Commission Rate Bundle (%)
                          <i className="bx bx-info-circle ms-1 text-muted"></i>
                        </Label>
                        <Input
                          name="commissionRateBundle"
                          type="number"
                          placeholder="12"
                          value={values.commissionRateBundle}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          Commission Rate One Time Product (%)
                          <i className="bx bx-info-circle ms-1 text-muted"></i>
                        </Label>
                        <Input
                          name="commissionRateOneTime"
                          type="number"
                          placeholder="15"
                          value={values.commissionRateOneTime}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Payout Cycle</Label>
                        <Input
                          type="select"
                          name="payoutCycle"
                          value={values.payoutCycle}
                          onChange={handleChange}
                        >
                          <option value="Weekly">Weekly</option>
                          <option value="Bi-Weekly">Bi-Weekly</option>
                          <option value="Monthly">Monthly</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Additional Notes</Label>
                        <Input
                          type="textarea"
                          name="additionalNotes"
                          rows={3}
                          placeholder="Any additional information..."
                          value={values.additionalNotes}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Collapse>
              </div>

              {/* Visibility And Features Section */}
              <div className="border rounded-3 p-3">
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setVisibilityOpen(!visibilityOpen)}
                >
                  <h5 className="mb-0 fw-semibold">Visibility And Features</h5>
                  <i className={`bx bx-chevron-${visibilityOpen ? "up" : "down"} font-size-20`}></i>
                </div>
                <Collapse isOpen={visibilityOpen}>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                      <div>
                        <h6 className="mb-1">Vendor Visibility</h6>
                        <p className="text-muted mb-0 font-size-12">Select Visibility Of Vendor</p>
                      </div>
                      <Switch
                        checked={values.vendorVisibility}
                        onChange={(checked) => setFieldValue("vendorVisibility", checked)}
                        onColor="#34c38f"
                        offColor="#f46a6a"
                        height={20}
                        width={44}
                        uncheckedIcon={false}
                        checkedIcon={false}
                      />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: "1px solid #eff2f7" }}>
                      <div>
                        <h6 className="mb-1">Verified Vendor</h6>
                        <p className="text-muted mb-0 font-size-12">Verify Vendor</p>
                      </div>
                      <Switch
                        checked={values.verifiedVendor}
                        onChange={(checked) => setFieldValue("verifiedVendor", checked)}
                        onColor="#34c38f"
                        offColor="#f46a6a"
                        height={20}
                        width={44}
                        uncheckedIcon={false}
                        checkedIcon={false}
                      />
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Auto-Approval For Products</h6>
                        <p className="text-muted mb-0 font-size-12">Auto approve products without permission</p>
                      </div>
                      <Switch
                        checked={values.autoApprovalProducts}
                        onChange={(checked) => setFieldValue("autoApprovalProducts", checked)}
                        onColor="#34c38f"
                        offColor="#f46a6a"
                        height={20}
                        width={44}
                        uncheckedIcon={false}
                        checkedIcon={false}
                      />
                    </div>
                  </div>
                </Collapse>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggle}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradiant-blue border-0">
                Create Vendor
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default AddNewVendor
